import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CalendarClock } from 'lucide-react';
import FactorykaOnDemand from "../assets/Factrika on-demand.png"
import ErrorModal from './ErrorModal';
import { useFloorManager } from '../context/FloorManagerContext';

const FloorManagerLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, logout, isLoggedIn, isHOD: contextIsHOD } = useFloorManager();
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showError, setShowError] = useState(false);

  const redirectUrl = location.state?.redirectUrl;

  useEffect(() => {
    // Only redirect away if the context itself confirms a valid session —
    // not a raw localStorage read, so this can't be fooled by a stale key.
    if (isLoggedIn) {
      if (redirectUrl) {
        navigate(redirectUrl);
      } else if (contextIsHOD) {
        navigate('/lkart/supervisor/requirements');
      } else {
        navigate('/lkart/supervisor/dashboard');
      }
    }
  }, [isLoggedIn, contextIsHOD, navigate, redirectUrl]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateEmail = (email) => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!validateEmail(credentials.email)) {
      setError("Please enter a Valid Email Address.");
      setShowError(true);
      return;
    }

    if (!credentials.password) {
      setError("Please Provide Password");
      setShowError(true);
      return;
    }

    setLoading(true);

    try {
      // Clear any stale floor-manager session before attempting a fresh login.
      logout();

      const { isHOD } = await login(credentials.email, credentials.password);

      setLoading(false);

      if (redirectUrl) {
        navigate(redirectUrl);
      } else if (isHOD) {
        navigate('/lkart/supervisor/requirements');
      } else {
        navigate('/lkart/supervisor/dashboard');
      }
    } catch (err) {
      console.error("Login error:", err);
      let errorMessage = 'Invalid Credentials. Please try again.';

      if (err.response) {
        errorMessage = err.response.data?.message || err.response.data?.error || errorMessage;
      } else if (err.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setShowError(true);
      setLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen overflow-y-auto pt-16 flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-xl shadow-lg">
          <div className="text-center flex flex-col items-center justify-center">
            <img
              src={FactorykaOnDemand}
              alt="FactoryKaOnDemand"
              width={220}
              height={220}
              className="object-contain"
            />
            <h2 className="mt-6 text-3xl font-bold text-gray-900">
              Department Login
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Enter your credentials to access the department dashboard
            </p>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleLogin}>
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={credentials.email}
                  onChange={handleChange}
                  className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter the Email Id"
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={credentials.password}
                  onChange={handleChange}
                  className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter Your Password"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 flex items-center justify-center"
              >
                {loading ? (
                  <span className="flex items-center">
                    <CalendarClock className="animate-spin -ml-1 mr-2 h-4 w-4" />
                    Logging in...
                  </span>
                ) : (
                  'Login'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
      <ErrorModal
        message={error}
        isOpen={showError}
        setIsOpen={setShowError}
      />
    </>
  );
};

export default FloorManagerLogin;